import { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Cell,
  AreaChart, Area,
} from "recharts";
import {
  runShadowModel, calculatePayout, getPosterior,
  FeatureDefinition, ShadowModelResult, PosteriorResult,
} from "../api";

interface Props {
  feature: FeatureDefinition | null;
}

const chartTooltip = {
  contentStyle: {
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: 12,
    fontSize: 12,
    color: "#111",
    padding: "8px 14px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  },
  labelStyle: { color: "#999" },
};

const formatValue = (v: any) => Number(v).toFixed(4);

function PipelineSteps({ currentStep }: { currentStep: number }) {
  const steps = [
    { num: 1, label: "Elicitation", desc: "Prior from expert" },
    { num: 2, label: "Bayesian Mix", desc: "Prior + ground truth" },
    { num: 3, label: "Monte Carlo", desc: "Synthetic column" },
    { num: 4, label: "Delivery", desc: "Coefficient shifts" },
  ];

  return (
    <div style={{ display: "flex", gap: 8 }}>
      {steps.map((step, i) => {
        const status = currentStep > step.num ? "complete" : currentStep === step.num ? "active" : "pending";
        return (
          <div key={step.num} className={`pipeline-step ${status}`} style={{ flex: 1 }}>
            <div className={`step-number ${status}`}>{status === "complete" ? "\u2713" : step.num}</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: status === "pending" ? "var(--text-muted)" : "var(--text-primary)" }}>
                {step.label}
              </div>
              <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{step.desc}</div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: status === "complete" ? "rgba(0,0,0,0.1)" : "var(--border-muted)", marginLeft: 4 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ShiftIndicator({ shift }: { shift: number }) {
  const isNeg = shift < -0.005;
  const isPos = shift > 0.005;
  const color = isNeg ? "var(--text-primary)" : isPos ? "var(--text-muted)" : "rgba(0,0,0,0.2)";
  const label = isNeg ? "Debiased" : isPos ? "Increased" : "Stable";
  return <span style={{ fontSize: 10, fontWeight: 500, color }}>{label}</span>;
}

export default function PerformanceDashboard({ feature }: Props) {
  const [shadowResult, setShadowResult] = useState<ShadowModelResult | null>(null);
  const [posteriorData, setPosteriorData] = useState<PosteriorResult | null>(null);
  const [payout, setPayout] = useState<{ estimated_payout: number; shapley_value: number } | null>(null);
  const [expertSignal, setExpertSignal] = useState(0.4);
  const [loading, setLoading] = useState(false);
  const [pipelineStep, setPipelineStep] = useState(1);

  const handleRunShadow = async () => {
    if (!feature) return;
    setLoading(true);

    // Step 2: Bayesian Mix
    setPipelineStep(2);
    try {
      const postRes = await getPosterior(feature.feature_id, expertSignal);
      setPosteriorData(postRes.data);

      // Step 3: Monte Carlo (happens server-side)
      setPipelineStep(3);

      // Step 4: Delivery
      setPipelineStep(4);
      const res = await runShadowModel(feature.feature_id, expertSignal);
      setShadowResult(res.data);

      const payoutRes = await calculatePayout(feature.feature_id);
      setPayout(payoutRes.data);
    } catch (err) {
      console.error("Pipeline failed:", err);
    }
    setLoading(false);
  };

  if (!feature) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 420, padding: "0 32px" }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, margin: "0 auto 24px",
            background: "linear-gradient(135deg, rgba(253,230,138,0.3), rgba(252,165,165,0.3))",
            backdropFilter: "blur(12px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" strokeLinecap="round">
              <path d="M18 20V10M12 20V4M6 20v-6"/>
            </svg>
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 8px", color: "var(--text-primary)" }}>
            Performance pipeline
          </h2>
          <p style={{ fontSize: 14, fontWeight: 300, color: "var(--text-muted)", margin: "0 0 24px", lineHeight: 1.6 }}>
            Once you've created a variable, run the full 4-step pipeline to see how it debiases existing model coefficients and computes the expert payout.
          </p>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 20px", borderRadius: 120,
            background: "rgba(0,0,0,0.03)", border: "1px solid var(--border-muted)",
            fontSize: 12, color: "var(--text-muted)",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(253,224,71,0.8)" }} />
            Awaiting variable
          </div>
        </div>
      </div>
    );
  }

  const weightData = shadowResult?.weight_history.map((w, i) => ({ step: i, weight: w })) || [];
  const baseShifts = shadowResult?.coefficient_shifts.filter(s => s.feature !== "Expert_Variable") || [];
  const expertShift = shadowResult?.coefficient_shifts.find(s => s.feature === "Expert_Variable");
  const shiftBarData = baseShifts.map(s => ({
    feature: s.feature.replace(/_/g, " "),
    shift: s.shift,
  }));

  // Build prior vs posterior PDF for overlay chart
  const posteriorChartData: { x: number; prior: number; posterior: number }[] = [];
  if (posteriorData) {
    const p = posteriorData.posterior;
    const range = Math.max(p.prior_std * 4, p.posterior_std * 8, 0.5);
    const center = p.prior_mean;
    for (let i = 0; i < 100; i++) {
      const x = center - range + (2 * range * i) / 99;
      const priorY = Math.exp(-0.5 * ((x - p.prior_mean) / p.prior_std) ** 2) / (p.prior_std * 2.507);
      const postY = Math.exp(-0.5 * ((x - p.posterior_mean) / p.posterior_std) ** 2) / (p.posterior_std * 2.507);
      posteriorChartData.push({ x: Math.round(x * 1000) / 1000, prior: Math.round(priorY * 1000) / 1000, posterior: Math.round(postY * 1000) / 1000 });
    }
  }

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid var(--border-muted)" }}>
        <h2 style={{ fontSize: 15, fontWeight: 400, margin: "0 0 4px" }}>Performance</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="tag" style={{ fontSize: 11 }}>{feature.name}</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>4-Step Pipeline</span>
        </div>
      </div>

      <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 28 }}>
        {/* Pipeline Progress */}
        <PipelineSteps currentStep={shadowResult ? 4 : pipelineStep} />

        {/* Controls */}
        <div className="card" style={{ padding: 24, borderRadius: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Signal Strength</span>
            <span style={{ fontSize: 28, fontWeight: 200, color: "var(--text-primary)" }}>
              {(expertSignal * 100).toFixed(0)}%
            </span>
          </div>
          <input type="range" min={0} max={100} value={Math.round(expertSignal * 100)}
            onChange={(e) => setExpertSignal(+e.target.value / 100)} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
            <span>No signal</span>
            <span>Strong signal</span>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="btn btn-outline" style={{ padding: "0 20px", height: 46, fontSize: 13 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Back
            </button>
            <button onClick={handleRunShadow} disabled={loading} className="btn btn-filled"
              style={{ flex: 1, height: 46, fontSize: 14, fontWeight: 400 }}>
              {loading ? "Running pipeline..." : "Continue"}
              {!loading && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Step 2: Posterior visualization */}
        {posteriorData && (
          <>
            <div className="divider" />
            <div className="animate-slide-up">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <span className="label">Step 2 — Bayesian Mix</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Prior + Ground Truth = Posterior</span>
              </div>

              {/* Prior vs Posterior overlay */}
              <div className="card" style={{ padding: "20px 16px 8px", borderRadius: 16 }}>
                <ResponsiveContainer width="100%" height={140}>
                  <AreaChart data={posteriorChartData}>
                    <XAxis dataKey="x" stroke="#bbb" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="#ddd" fontSize={10} tickLine={false} axisLine={false} />
                    <Tooltip {...chartTooltip} />
                    <defs>
                      <linearGradient id="priorGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(0,0,0,0.08)" />
                        <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                      </linearGradient>
                      <linearGradient id="postGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(0,0,0,0.18)" />
                        <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="prior" stroke="rgba(0,0,0,0.15)" strokeWidth={1} fill="url(#priorGrad)" name="Prior" />
                    <Area type="monotone" dataKey="posterior" stroke="rgba(0,0,0,0.4)" strokeWidth={2} fill="url(#postGrad)" name="Posterior" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Posterior params */}
              <div className="grid-cells" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", marginTop: 12 }}>
                {[
                  { label: "Prior \u03bc", value: posteriorData.posterior.prior_mean.toFixed(4) },
                  { label: "Posterior \u03bc", value: posteriorData.posterior.posterior_mean.toFixed(4) },
                  { label: "Prior weight", value: `${(posteriorData.posterior.prior_weight * 100).toFixed(1)}%` },
                  { label: "Data weight", value: `${(posteriorData.posterior.data_weight * 100).toFixed(1)}%` },
                ].map((item) => (
                  <div key={item.label} style={{ padding: 14 }}>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3, fontWeight: 500 }}>{item.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 300, color: "var(--text-primary)" }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Step 3: Synthetic Data Preview */}
        {posteriorData && posteriorData.synthetic_preview.length > 0 && (
          <>
            <div className="divider" />
            <div className="animate-slide-up">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <span className="label">Step 3 — Monte Carlo</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  Synthetic column generated ({posteriorData.synthetic_column_distribution.low}L / {posteriorData.synthetic_column_distribution.med}M / {posteriorData.synthetic_column_distribution.high}H)
                </span>
              </div>

              <div className="card" style={{ borderRadius: 16, overflow: "hidden", maxHeight: 260, overflowY: "auto" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>#</th>
                      {Object.keys(posteriorData.synthetic_preview[0]).map(col => (
                        <th key={col} className={col === "Expert_Variable" ? "highlight-col" : ""}>
                          {col.replace(/_/g, " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {posteriorData.synthetic_preview.slice(0, 10).map((row, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        {Object.entries(row).map(([col, val]) => (
                          <td key={col} className={col === "Expert_Variable" ? "highlight-col" : ""}>
                            {typeof val === "number" ? (Number.isInteger(val) ? val : val.toFixed(3)) : val}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {shadowResult && (
          <>
            <div className="divider" />

            {/* Step 4: Coefficient Shift Table */}
            <div className="animate-slide-up">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <span className="label">Step 4 — Delivery</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Coefficient debiasing results</span>
              </div>

              <div className="card" style={{ borderRadius: 16, overflow: "hidden" }}>
                {/* Table header */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr",
                  padding: "10px 16px",
                  background: "rgba(0,0,0,0.02)",
                  borderBottom: "1px solid var(--border-muted)",
                  fontSize: 10,
                  fontWeight: 600,
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}>
                  <span>Variable</span>
                  <span style={{ textAlign: "right" }}>Before</span>
                  <span style={{ textAlign: "right" }}>After</span>
                  <span style={{ textAlign: "right" }}>Shift</span>
                  <span style={{ textAlign: "right" }}>Status</span>
                </div>

                {baseShifts.map((s, i) => (
                  <div key={s.feature} style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr",
                    padding: "12px 16px",
                    borderBottom: i < baseShifts.length - 1 ? "1px solid var(--border-muted)" : "none",
                    background: Math.abs(s.shift) > 0.02 ? "rgba(0,0,0,0.015)" : "transparent",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 400 }}>{s.feature.replace(/_/g, " ")}</span>
                    <span style={{ textAlign: "right", fontSize: 13, fontWeight: 300, fontFamily: "monospace" }}>{s.before.toFixed(4)}</span>
                    <span style={{ textAlign: "right", fontSize: 13, fontWeight: 300, fontFamily: "monospace" }}>{s.after.toFixed(4)}</span>
                    <span style={{
                      textAlign: "right", fontSize: 13, fontWeight: 400, fontFamily: "monospace",
                      color: s.shift < -0.005 ? "var(--text-primary)" : "var(--text-muted)",
                    }}>
                      {s.shift > 0 ? "+" : ""}{s.shift.toFixed(4)}
                    </span>
                    <span style={{ textAlign: "right" }}><ShiftIndicator shift={s.shift} /></span>
                  </div>
                ))}

                {expertShift && (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr",
                    padding: "12px 16px",
                    borderTop: "2px solid rgba(0,0,0,0.08)",
                    background: "rgba(0,0,0,0.02)",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{feature.name}</span>
                    <span style={{ textAlign: "right", fontSize: 12, color: "var(--text-muted)" }}>&mdash;</span>
                    <span style={{ textAlign: "right", fontSize: 13, fontWeight: 400, fontFamily: "monospace" }}>{expertShift.after.toFixed(4)}</span>
                    <span style={{ textAlign: "right", fontSize: 13, fontWeight: 400, fontFamily: "monospace" }}>
                      {expertShift.after > 0 ? "+" : ""}{expertShift.after.toFixed(4)}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)" }}>NEW</span>
                    </span>
                  </div>
                )}
              </div>

              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10, lineHeight: 1.5 }}>
                Negative shifts = variables that were <strong>inflated</strong> due to omitted variable bias.
                The expert variable absorbs the variance they were incorrectly claiming.
              </div>
            </div>

            <div className="divider" />

            {/* Credit Redistribution bar chart */}
            {shiftBarData.length > 0 && (
              <div>
                <span className="label" style={{ display: "block", marginBottom: 14 }}>Credit Redistribution</span>
                <div className="card" style={{ padding: "20px 16px 8px", borderRadius: 16 }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={shiftBarData} layout="vertical" barCategoryGap="25%">
                      <XAxis type="number" stroke="#bbb" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="feature" stroke="#bbb" fontSize={10} width={100} tickLine={false} axisLine={false} />
                      <Tooltip {...chartTooltip} formatter={(v: any) => Number(v).toFixed(4)} />
                      <ReferenceLine x={0} stroke="rgba(0,0,0,0.12)" />
                      <Bar dataKey="shift" radius={[0, 4, 4, 0]}>
                        {shiftBarData.map((entry, i) => (
                          <Cell key={i} fill={entry.shift < 0 ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.08)"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="divider" />

            {/* Quality Score — inspired by dataset health check */}
            <div className="card" style={{ padding: 32, borderRadius: 24, textAlign: "center" }}>
              <div className="label" style={{ marginBottom: 20 }}>Debiasing Quality</div>
              <div style={{
                width: 80, height: 80, borderRadius: 24, margin: "0 auto 16px",
                background: shadowResult.delta_auc > 0.005
                  ? "linear-gradient(135deg, rgba(134,239,172,0.2), rgba(134,239,172,0.05))"
                  : shadowResult.delta_auc > -0.005
                    ? "linear-gradient(135deg, rgba(253,224,71,0.2), rgba(253,224,71,0.05))"
                    : "linear-gradient(135deg, rgba(252,165,165,0.2), rgba(252,165,165,0.05))",
                border: "1px solid rgba(255,255,255,0.5)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
              }}>
                <span style={{ fontSize: 36, fontWeight: 700, color: "var(--text-primary)" }}>
                  {shadowResult.delta_auc > 0.01 ? "A" : shadowResult.delta_auc > 0.005 ? "B" : shadowResult.delta_auc > -0.005 ? "C" : "D"}
                </span>
              </div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, maxWidth: 340, margin: "0 auto" }}>
                {shadowResult.delta_auc > 0.005
                  ? <>Your debiasing quality is <strong>excellent</strong>. The expert variable significantly corrects omitted variable bias across existing coefficients.</>
                  : shadowResult.delta_auc > -0.005
                    ? <>Your debiasing quality is <strong>good</strong>. Coefficients are now redistributed more honestly, even with minimal AUC change.</>
                    : <>Your variable may not carry enough signal. Consider refining the elicitation.</>}
              </div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                marginTop: 20, padding: "10px 24px", borderRadius: 120,
                background: "rgba(0,0,0,0.03)", border: "1px solid var(--border-muted)",
              }}>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Delta AUC</span>
                <span style={{ fontSize: 20, fontWeight: 300, letterSpacing: "-0.03em" }}>
                  {shadowResult.delta_auc > 0 ? "+" : ""}{shadowResult.delta_auc.toFixed(4)}
                </span>
              </div>
            </div>

            <div className="divider" />

            {/* Weight Decay */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <span className="label">Weight Decay</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {shadowResult.weight_history[0]?.toFixed(4)} → {shadowResult.weight_history[shadowResult.weight_history.length - 1]?.toFixed(4)}
                </span>
              </div>
              <div className="card" style={{ padding: "20px 16px 8px", borderRadius: 16 }}>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={weightData}>
                    <XAxis dataKey="step" stroke="#bbb" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="#ddd" fontSize={10} tickLine={false} axisLine={false} domain={[0, "auto"]} />
                    <Tooltip {...chartTooltip} formatter={formatValue} />
                    <ReferenceLine y={0.4} stroke="rgba(0,0,0,0.06)" strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="weight" stroke="rgba(0,0,0,0.35)" strokeWidth={1.5} dot={false} animationDuration={1500} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="divider" />

            {/* Payout */}
            <div className="animate-slide-up">
              <span className="label" style={{ display: "block", marginBottom: 16 }}>Expert Payout</span>
              <div className="grid-cells" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div style={{ padding: 22, textAlign: "center" }}>
                  <div className="label" style={{ marginBottom: 8 }}>Shapley Value</div>
                  <div style={{ fontSize: 32, fontWeight: 200 }}>{shadowResult.shapley_value.toFixed(4)}</div>
                </div>
                <div style={{ padding: 22, textAlign: "center" }}>
                  <div className="label" style={{ marginBottom: 8 }}>Estimated Royalty</div>
                  <div style={{ fontSize: 32, fontWeight: 200 }}>${payout?.estimated_payout.toFixed(2) || "\u2014"}</div>
                </div>
              </div>
              <div style={{ textAlign: "center", fontSize: 11, color: "var(--text-muted)", marginTop: 10 }}>
                Based on marginal debiasing contribution at $100/basis point
              </div>
            </div>

            <div className="divider" />

            {/* Summary */}
            <div>
              <span className="label" style={{ display: "block", marginBottom: 16 }}>Summary</span>
              <div className="grid-cells" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
                {[
                  { label: "Champion", value: shadowResult.champion_auc.toFixed(4) },
                  { label: "Challenger", value: shadowResult.challenger_auc.toFixed(4) },
                  { label: "Weight", value: shadowResult.weight_history[shadowResult.weight_history.length - 1]?.toFixed(4) },
                  { label: "Shapley", value: shadowResult.shapley_value.toFixed(4) },
                ].map((item) => (
                  <div key={item.label} style={{ padding: 16 }}>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4, fontWeight: 500 }}>{item.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 300 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
